import React, { useRef } from 'react'
import { Box, IconButton, Typography } from '@mui/material'
import { Close, DragHandle, Undo } from '@mui/icons-material'
import { grey } from '@mui/material/colors'
import Key from './Key'
import { useDrag } from 'react-dnd'
import { ItemTypes } from '../../utils/dnd/constants'
import Drop from './Drop'
import useIsMobile from '../../hooks/useIsMobile'

const Item = ({
  canReorder,
  canRemove,
  altText,
  canEdit,
  i,
  editableKeys,
  getCanEditKey,
  item,
  width,
  allowDuplicates,
  removeOptions,
  getItemOnRemove,
  canUndoRemove,
  disabled,
  removeItem,
  setItem,
  editItem,
  mergeEditItem,
  drag,
  preview,
  isDragging,
  list,
  addItemOnEnter,
  dragOnly,
  displayOverride,
}) => {
  const dragRef = useRef(null)
  const previewRef = useRef(null)

  drag(dragRef)
  preview(previewRef)

  const isMobile = useIsMobile()

  const mobileStyles = {
    border: `1px solid ${grey[600]}`,
    p: 1,
    borderRadius: '4px',
    // borderTop: i === 0 ? `1px solid ${grey[600]}` : 'none',
    py: 1,
    alignItems: 'flex-start',
    my: 1,
    backgroundColor: 'white',
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        opacity: isDragging ? 0 : 1,
        height: isDragging ? 0 : null,

        ...(isMobile ? mobileStyles : {}),
      }}
      key={i}
      ref={previewRef}
    >
      {canReorder && (
        <Box
          sx={{
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'move',
            position: 'relative',
            zIndex: 2,
          }}
          ref={dragRef}
        >
          <DragHandle />
        </Box>
      )}

      {dragOnly && displayOverride(item, i)}

      {!dragOnly && (
        <>
          <Box
            sx={{
              border: `1px solid ${grey[400]}`,
              width: '30px',
              height: '30px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              backgroundColor: 'white',
              mr: 1,
              mt: isMobile ? 1 : null,
            }}
          >
            <Typography sx={{ fontWeight: 'bold' }}>{i + 1}</Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              flexGrow: 1,
            }}
          >
            {editableKeys.map((value, keyIndex) => (
              <Key
                key={keyIndex}
                value={value}
                keyIndex={keyIndex}
                getCanEditKey={getCanEditKey}
                item={item}
                editableKeys={editableKeys}
                width={width}
                canEdit={canEdit}
                allowDuplicates={allowDuplicates}
                editItem={editItem}
                mergeEditItem={mergeEditItem}
                i={i}
                list={list}
                addItemOnEnter={addItemOnEnter}
              />
            ))}
          </Box>

          {canRemove && !disabled && (
            <IconButton
              onClick={() => {
                if (!getItemOnRemove || !canUndoRemove || !removeOptions)
                  return removeItem(i)
                let newValue

                if (getItemOnRemove) {
                  newValue = getItemOnRemove(item)

                  if (newValue) {
                    return setItem(newValue, i)
                  }
                  return removeItem(i)
                }

                newValue =
                  canUndoRemove &&
                  removeOptions &&
                  item[removeOptions.key] === removeOptions.value
                    ? !removeOptions.value
                    : removeOptions.value

                return editItem(item, i, removeOptions.key, newValue)
              }}
            >
              {canUndoRemove &&
              removeOptions &&
              item[removeOptions.key] === removeOptions.value ? (
                <Undo />
              ) : (
                <Close />
              )}
            </IconButton>
          )}

          {!canRemove && (
            <Box
              sx={{
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                left: '8px',
                fontSize: '14px',
              }}
            >
              {altText && <i>{altText}</i>}
            </Box>
          )}
        </>
      )}
    </Box>
  )
}

const DragWrapper = (props) => {
  const [{ isDragging }, drag, preview] = useDrag(
    () => ({
      type: ItemTypes.LIST_ITEM,
      canDrag: true,
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
      item: props.item,
    }),
    [props.item, props.list]
  )

  return (
    <>
      {props.canReorder && props.i === 0 && (
        <Drop id={0} onDrop={props.onDrop} />
      )}
      <Item {...props} drag={drag} preview={preview} isDragging={isDragging} />
      {!isDragging && props.canReorder && (
        <Drop id={props.item.id} onDrop={props.onDrop} />
      )}
    </>
  )
}

export default DragWrapper
